/**
 * Single source of truth for the car-service sales WhatsApp line.
 * Used by header, footer, lookup widget, sticky CTA, modals, etc.
 * Update the number / default message here only.
 *
 * As of 2026-05-21, reverted to the legacy inspection support line
 * (+91 73380 73380) per ops decision — keeps every /carservice LP
 * CTA consistent across header, footer, sticky bar, FAB and modals.
 */
import { getAttribution } from "../../utils/gtag";

export const WHATSAPP_NUMBER = "917338073380";

// Voice-call line — same number as WhatsApp for v1 (LP-dedicated for Google Ads attribution).
export const PHONE_NUMBER = "+917338073380";
export const PHONE_NUMBER_DISPLAY = "+91 73380 73380";

export const WHATSAPP_DEFAULT_MESSAGE =
  "Hi, I am interested in car service. Let me know the pricing!";

export function buildWhatsAppHref(message = WHATSAPP_DEFAULT_MESSAGE) {
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
}

export const WHATSAPP_HREF = buildWhatsAppHref();

/**
 * Slugify a city (object|string) for the CRM ref token. Falls back to the
 * session-persisted /carservice city selection, then "na".
 */
function carServiceCitySlug(city) {
  let raw = null;
  if (city && typeof city === "object") raw = city.slug || city.name;
  else if (typeof city === "string") raw = city;
  // URL ?city=<slug> is authoritative for campaign landings and is available
  // SYNCHRONOUSLY — before the async /cities fetch resolves `selectedCity`.
  // This is what stops the header/footer tokens arriving as "na" when the
  // visitor clicks before (or without) picking a city from the dropdown.
  if (!raw && typeof window !== "undefined") {
    try {
      const p = new URLSearchParams(window.location.search).get("city");
      if (p) raw = p;
    } catch { /* URL unavailable */ }
  }
  if (!raw && typeof sessionStorage !== "undefined") {
    try {
      const cached = JSON.parse(sessionStorage.getItem("wd_cs_selected_city") || "null");
      raw = cached?.slug || cached?.name || null;
    } catch { /* session unavailable */ }
  }
  return raw ? String(raw).toLowerCase().trim().replace(/\s+/g, "-") : "na";
}

/**
 * City-aware WhatsApp href for /carservice CTAs. Appends the CRM ref token
 *   [WD-CS:<city>:<cta>:<clickparam>:<clickid>]
 * to the prefilled text so WhatsApp enquiries carry the selected city, the
 * CTA that was clicked, AND the *typed* Google/Meta click id so the CRM can
 * upload it to the correct offline-conversion API.
 *
 *   <city>       — slug of the selected city (or "na")
 *   <cta>        — which CTA fired (header|lookup_widget|footer|footer_nav|
 *                  sticky|fab|modal-error|doorstep|generic)
 *   <clickparam> — the click-id PARAM NAME we read, in priority order
 *                  gclid > gbraid > wbraid > fbclid. Empty for organic.
 *   <clickid>    — the click-id VALUE. Empty for organic.
 *
 * The CRM MUST be told which kind of click id it is (gclid vs gbraid vs
 * wbraid vs fbclid) — Google/Meta reject a conversion uploaded against the
 * wrong click-id type. Organic visits emit empty <clickparam>/<clickid>
 * segments (e.g. `[WD-CS:delhi:header::]`).
 *
 * NOTE: the `clickType` argument is the CTA location (kept for call-site
 * back-compat); it maps to the <cta> segment.
 *
 * @param {{ message?:string, city?:(object|string), clickType?:string }} [opts]
 */
const CLICK_PARAM_PRIORITY = ["gclid", "gbraid", "wbraid", "fbclid"];

export function buildCarServiceWhatsAppHref({
  message = WHATSAPP_DEFAULT_MESSAGE,
  city,
  clickType = "generic",
} = {}) {
  const cta = clickType;
  let clickParam = "";
  let clickId = "";
  try {
    const a = getAttribution() || {};
    for (const key of CLICK_PARAM_PRIORITY) {
      const v = a[key];
      if (v && v !== "none" && v !== "null" && v !== "undefined") {
        clickParam = key;
        clickId = v;
        break;
      }
    }
  } catch { /* attribution optional */ }
  const ref = `[WD-CS:${carServiceCitySlug(city)}:${cta}:${clickParam}:${clickId}]`;
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`${message}\n\n${ref}`)}`;
}
